import React, { useEffect, useRef, useState } from 'react'

import { CalendarIcon, X, Info, Gem, Check, Pause, Play } from 'lucide-react'
import { DateTime } from 'luxon'
import type { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import type { Advert } from '@/core/api/entities/campaign/advert'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import type { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
} from '@/shared/components/atoms/Form'
import { Input } from '@/shared/components/atoms/Input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import Tooltip from '@/shared/components/atoms/Tooltip'
import { Calendar } from '@/shared/components/organisms/Calendar/Calendar'

import { DAYS_OF_WEEK } from '../constants/daysOfWeek'
import { HOUR_OPTIONS } from '../constants/timeOptions'
import { type CampaignConfigFormValues } from '../schemas/createCampaignSchema'

interface CampaignConfigFormProps {
	form: UseFormReturn<CampaignConfigFormValues>
	onSubmit: (values: CampaignConfigFormValues) => void | Promise<void>
	onCancel: () => void
	submitLabel: string
	isSubmitting?: boolean
	isEdit: boolean
	readOnly: boolean
	onReadOnlyMobileAdvertClick?: (advert: Advert) => void
	activeAdvertId?: number | null
	isActiveAdvertPlaying?: boolean
}

export const CampaignConfigForm: React.FC<CampaignConfigFormProps> = ({
	form,
	onSubmit,
	onCancel,
	submitLabel,
	isSubmitting,
	isEdit,
	readOnly = false,
	onReadOnlyMobileAdvertClick,
	activeAdvertId,
	isActiveAdvertPlaying = false,
}) => {
	const { t } = useTranslation('campaigns')
	const navigate = useNavigate()
	const { features } = usePermission()
	const canCreateCampaign = features.campaign.create
	const showCampaignCapabilityLock = !readOnly && !canCreateCampaign
	const RECURRENCE_TYPES = [
		{ value: 'recurrent', label: t('config.form.recurrenceTypes.recurrent') },
		{
			value: 'one_per_day',
			label: t('config.form.recurrenceTypes.one_per_day'),
		},
		{
			value: 'one_time_only',
			label: t('config.form.recurrenceTypes.one_time_only'),
		},
	] as const

	const rruleType = form.watch('rruleType')
	const exdate = form.watch('exdate')
	const startDate = form.watch('startDate')
	const endDate = form.watch('endDate')
	const playEvery = form.watch('playEvery')
	const playEveryUnit = form.watch('playEveryUnit')

	// Keep an editable string so the user can clear the field while typing.
	const [playEveryDraft, setPlayEveryDraft] = useState<string>(
		playEvery === null || playEvery === undefined ? '' : String(playEvery)
	)
	const playEveryFocusedRef = useRef(false)

	const commitPlayEveryDraft = () => {
		const raw = playEveryDraft.trim()
		const next = Math.max(1, parseInt(raw || '1', 10) || 1)
		form.setValue('playEvery', next, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setPlayEveryDraft(String(next))
	}

	useEffect(() => {
		if (playEveryFocusedRef.current) return
		setPlayEveryDraft(
			playEvery === null || playEvery === undefined ? '' : String(playEvery)
		)
	}, [playEvery])
	const priority = form.watch('priority')
	const interruptsMusic = form.watch('interruptsMusic')
	const advertsCount = useSelector((state: RootState) => {
		const { adverts, advertIds } = state.campaignFlow
		return adverts.length > 0 ? adverts.length : advertIds.length
	})
	const adverts = useSelector((state: RootState) => state.campaignFlow.adverts)

	const formatDate = (dateValue: Date | null | undefined): string => {
		if (!dateValue) return t('notSet')
		return DateTime.fromJSDate(dateValue).toFormat('EEE d, MMM yyyy')
	}

	const playEveryLabel =
		playEvery && playEveryUnit
			? `${playEvery} ${
					playEveryUnit === 'hours'
						? t('config.form.units.hours')
						: t('config.form.units.minutes')
				}`
			: t('notSet')
	const recurrenceLabel =
		RECURRENCE_TYPES.find((type) => type.value === rruleType)?.label ||
		t('notSet')
	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit((values) => {
					// Ensure the draft value is committed even when user submits via Enter
					// (submit can happen without blur).
					commitPlayEveryDraft()
					return onSubmit({
						...values,
						playEvery:
							values.playEvery ??
							Math.max(1, parseInt(playEveryDraft.trim() || '1', 10) || 1),
					})
				})}
				className="space-y-6 pb-24 md:pb-0"
			>
				{!readOnly && (
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('config.form.campaignName')}</FormLabel>
								<FormControl>
									<Input
										placeholder={t('config.form.campaignNamePlaceholder')}
										{...field}
										className="bg-outline"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
				<div className="space-y-3">
					{!readOnly ? (
						<>
							<FormLabel className="mb-1 md:block hidden">
								{t('config.form.recurrenceType')}
							</FormLabel>

							<div className="w-full rounded-full border border-border bg-background p-1 grid grid-cols-3 gap-1">
								{RECURRENCE_TYPES.map((type) => (
									<button
										key={type.value}
										type="button"
										onClick={() => form.setValue('rruleType', type.value)}
										className={`h-10 rounded-full text-sm md:text-base font-medium transition-colors min-w-0 px-2 ${
											rruleType === type.value
												? 'bg-outline text-foreground'
												: 'text-muted-foreground'
										}`}
									>
										<span className="block truncate">{type.label}</span>
									</button>
								))}
							</div>
						</>
					) : null}
				</div>
				{showCampaignCapabilityLock && (
					<div className="pt-6 pb-4 flex flex-col items-center text-center">
						<div className="w-[100px] h-[100px] rounded-3xl bg-outline flex items-center justify-center mb-6">
							<Gem className="w-10 h-10 text-foreground" />
						</div>
						<p className="text-4xl font-medium tracking-tight max-w-[320px]">
							{t('config.capability.title')}
						</p>
						<p className="text-sm text-muted-foreground mt-3 max-w-[320px]">
							{t('config.capability.description')}
						</p>
						<Button
							type="button"
							variant="outline"
							className="mt-8 rounded-full px-6"
							onClick={() =>
								navigate('/account?tab=subscription&action=upgrade')
							}
						>
							{t('config.capability.upgrade')}
						</Button>
					</div>
				)}
				{!showCampaignCapabilityLock &&
					!readOnly &&
					rruleType === 'one_time_only' && (
						<FormField
							control={form.control}
							name="startDate"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('config.form.date')}</FormLabel>
									<Popover>
										<PopoverTrigger asChild>
											<FormControl>
												<Button
													variant="secondary"
													className="w-full justify-start font-normal"
												>
													<CalendarIcon className="mr-2 h-4 w-4" />
													{field.value ? (
														DateTime.fromJSDate(field.value).toFormat(
															'EEE d LLL, yyyy'
														)
													) : (
														<span>{t('config.form.pickDate')}</span>
													)}
												</Button>
											</FormControl>
										</PopoverTrigger>
										<PopoverContent className="w-auto p-0">
											<Calendar
												mode="single"
												selected={field.value ?? undefined}
												onSelect={field.onChange}
												disabled={(day) => {
													// In create mode, prevent selecting past dates.
													if (isEdit) return false
													const date = DateTime.fromJSDate(day).startOf('day')
													const today = DateTime.now().startOf('day')
													return date < today
												}}
												initialFocus
											/>
										</PopoverContent>
									</Popover>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}{' '}
				{!showCampaignCapabilityLock &&
					!readOnly &&
					rruleType !== 'one_time_only' && (
						<div className="flex flex-row justify-between md:justify-start md:grid md:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="startDate"
								render={({ field }) => (
									<FormItem className="w-full md:w-auto">
										<FormLabel>{t('config.form.startDate')}</FormLabel>
										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														className="w-full justify-start font-normal"
													>
														<CalendarIcon className="mr-2 h-4 w-4" />
														{field.value ? (
															DateTime.fromJSDate(field.value).toFormat(
																'EEE d LLL, yyyy'
															)
														) : (
															<span>{t('config.form.pickDate')}</span>
														)}
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0">
												<Calendar
													mode="single"
													selected={field.value ?? undefined}
													onSelect={field.onChange}
													disabled={(day) => {
														// In create mode, prevent selecting past dates.
														if (isEdit) return false
														const date = DateTime.fromJSDate(day).startOf('day')
														const today = DateTime.now().startOf('day')
														return date < today
													}}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="endDate"
								render={({ field }) => (
									<FormItem className="w-full md:w-auto">
										<FormLabel>{t('config.form.endDate')}</FormLabel>
										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														className="w-full justify-start font-normal"
													>
														<CalendarIcon className="mr-2 h-4 w-4" />
														{field.value ? (
															DateTime.fromJSDate(field.value).toFormat(
																'EEE d LLL, yyyy'
															)
														) : (
															<span>{t('config.form.pickDate')}</span>
														)}
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0">
												<Calendar
													mode="single"
													selected={field.value ?? undefined}
													onSelect={field.onChange}
													disabled={(day) => {
														if (!startDate) return false
														const candidate =
															DateTime.fromJSDate(day).startOf('day')
														const start =
															DateTime.fromJSDate(startDate).startOf('day')
														// Disable dates on or before the selected start date
														return candidate <= start
													}}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)}
				{readOnly && (
					<div className="hidden md:flex flex-wrap md:flex-nowrap md:items-end gap-4 lg:gap-10 lg:text-sm text-xs mb-14">
						<div className="w-[calc(50%-0.5rem)] md:w-auto">
							<span className="text-muted-foreground block mb-1">
								{t('list.startDate')}
							</span>
							<span className="lg:font-medium text-xs">
								{formatDate(startDate)}
							</span>
						</div>
						<div className="w-[calc(50%-0.5rem)] md:w-auto">
							<span className="text-muted-foreground block mb-1">
								{t('list.endDate')}
							</span>
							<span className="font-medium">{formatDate(endDate)}</span>
						</div>
						{/* Time frequency */}
						<div className="w-[calc(50%-0.5rem)] md:w-auto">
							<span className="text-muted-foreground block mb-1">
								{t('config.form.playEvery')}
							</span>
							<span className="font-medium">{playEveryLabel}</span>
						</div>

						<div className="w-[calc(50%-0.5rem)] md:w-auto">
							<span className="text-muted-foreground block mb-1">
								{t('list.adsIncluded')}
							</span>
							<span className="font-medium">{advertsCount}</span>
						</div>
					</div>
				)}
				{readOnly && (
					<div className="md:hidden space-y-8 mb-10">
						<div className="space-y-5">
							<h3 className="text-base font-semibold tracking-tight">
								{t('config.mobile.infoTitle')}
							</h3>
							<div className="space-y-1.5">
								<p className="text-xs text-muted-foreground">
									{t('config.form.recurrenceType')}
								</p>
								<p className="text-sm font-medium leading-tight">
									{recurrenceLabel}
								</p>
							</div>
							<div className="space-y-1.5">
								<p className="text-xs text-muted-foreground">
									{t('config.form.startDate')}
								</p>
								<p className="text-sm font-medium leading-tight">
									{formatDate(startDate)}
								</p>
							</div>
							<div className="space-y-1.5">
								<p className="text-xs text-muted-foreground">
									{t('config.form.endDate')}
								</p>
								<p className="text-sm font-medium leading-tight">
									{formatDate(endDate)}
								</p>
							</div>
							<div className="space-y-1.5">
								<p className="text-xs text-muted-foreground">
									{t('config.form.playEvery')}
								</p>
								<p className="text-sm font-medium leading-tight">
									{playEveryLabel}
								</p>
							</div>
							{priority && (
								<div className="flex items-center gap-2 pt-1">
									<Check className="w-4 h-4" />
									<span className="text-sm font-medium leading-tight">
										{t('config.form.prioritize')}
									</span>
								</div>
							)}
							{interruptsMusic && (
								<div className="flex items-center gap-2">
									<Check className="w-4 h-4" />
									<span className="text-sm font-medium leading-tight">
										{t('config.form.interruptsMusic')}
									</span>
								</div>
							)}
						</div>

						<div className="space-y-4">
							<h3 className="text-base font-semibold tracking-tight">
								{t('config.mobile.adsTitle')}
							</h3>
							{adverts.length > 0 ? (
								<div className="space-y-2">
									<div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 text-sm text-muted-foreground">
										<span>Name</span>
										<span>Start</span>
										<span>End</span>
									</div>
									{adverts.map((advert) => (
										<div
											key={advert.id}
											className={`grid grid-cols-[1.4fr_1fr_1fr_auto] gap-2 items-center py-1 ${
												onReadOnlyMobileAdvertClick
													? 'cursor-pointer rounded-md hover:bg-accent/20 transition-colors'
													: ''
											}`}
											role={onReadOnlyMobileAdvertClick ? 'button' : undefined}
											tabIndex={onReadOnlyMobileAdvertClick ? 0 : undefined}
											onClick={() => onReadOnlyMobileAdvertClick?.(advert)}
											onKeyDown={(event) => {
												if (!onReadOnlyMobileAdvertClick) return
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault()
													onReadOnlyMobileAdvertClick(advert)
												}
											}}
										>
											<p className="text-base truncate">{advert.name}</p>
											<p className="text-base text-muted-foreground truncate">
												{advert.dateStart
													? advert.dateStart.toFormat('MMM d, yyyy')
													: '-'}
											</p>
											<p className="text-base text-muted-foreground truncate">
												{advert.dateEnd
													? advert.dateEnd.toFormat('MMM d, yyyy')
													: '-'}
											</p>
											{onReadOnlyMobileAdvertClick && (
												<div className="flex items-center justify-end">
													{activeAdvertId === advert.id &&
													isActiveAdvertPlaying ? (
														<Pause className="w-4 h-4 text-foreground" />
													) : (
														<Play className="w-4 h-4 text-muted-foreground" />
													)}
												</div>
											)}
										</div>
									))}
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									{t('upload.noAdsAssigned')}
								</p>
							)}
						</div>
					</div>
				)}
				{!showCampaignCapabilityLock &&
					!readOnly &&
					rruleType === 'recurrent' && (
						<>
							<div className="flex gap-4">
								<FormField
									control={form.control}
									name="playEvery"
									render={({ field }) => (
										<FormItem className="flex-1">
											<FormLabel>{t('config.form.playEvery')}</FormLabel>
											<FormControl>
												<>
													<div className="md:hidden flex items-center rounded-xl border border-border bg-outline p-1">
														<Input
															type="number"
															min={1}
															{...field}
															value={playEveryDraft}
															onChange={(e) => {
																setPlayEveryDraft(e.target.value)
															}}
															onFocus={() => {
																playEveryFocusedRef.current = true
															}}
															onBlur={() => {
																playEveryFocusedRef.current = false
																commitPlayEveryDraft()
															}}
															className="h-10 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-3"
														/>
														<div className="shrink-0 rounded-lg bg-muted p-1 grid grid-cols-2 gap-1">
															<button
																type="button"
																onClick={() =>
																	form.setValue('playEveryUnit', 'minutes')
																}
																className={`h-8 px-3 rounded-md text-xs md:text-sm font-medium transition-colors ${
																	playEveryUnit === 'minutes'
																		? 'bg-primary text-background'
																		: 'text-muted-foreground'
																}`}
															>
																{t('config.form.units.minutes')}
															</button>
															<button
																type="button"
																onClick={() =>
																	form.setValue('playEveryUnit', 'hours')
																}
																className={`h-8 px-3 rounded-md text-xs md:text-sm font-medium transition-colors ${
																	playEveryUnit === 'hours'
																		? 'bg-primary text-background'
																		: 'text-muted-foreground'
																}`}
															>
																{t('config.form.units.hours')}
															</button>
														</div>
													</div>
													<Input
														type="number"
														min={1}
														{...field}
														value={playEveryDraft}
														onChange={(e) => {
															setPlayEveryDraft(e.target.value)
														}}
														onFocus={() => {
															playEveryFocusedRef.current = true
														}}
														onBlur={() => {
															playEveryFocusedRef.current = false
															commitPlayEveryDraft()
														}}
														className="hidden md:block"
													/>
												</>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="playEveryUnit"
									render={({ field }) => (
										<FormItem className="w-32 hidden md:block">
											<FormLabel>&nbsp;</FormLabel>
											<Select
												onValueChange={field.onChange}
												value={field.value}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="minutes">
														{t('config.form.units.minutes')}
													</SelectItem>
													<SelectItem value="hours">
														{t('config.form.units.hours')}
													</SelectItem>
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="flex gap-4">
								<FormField
									control={form.control}
									name="timeFrom"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('config.form.from')}</FormLabel>
											<Select
												onValueChange={field.onChange}
												value={field.value}
											>
												<SelectTrigger className="min-w-[160px] bg-outline">
													<SelectValue
														placeholder={t('config.form.selectTime')}
													/>
												</SelectTrigger>
												<SelectContent>
													{HOUR_OPTIONS.map((time) => (
														<SelectItem key={time} value={time}>
															{time.split(':')[0]}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="timeTo"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('config.form.to')}</FormLabel>
											<Select
												onValueChange={field.onChange}
												value={field.value}
											>
												<SelectTrigger className="min-w-[160px] bg-outline">
													<SelectValue
														placeholder={t('config.form.selectTime')}
													/>
												</SelectTrigger>
												<SelectContent>
													{HOUR_OPTIONS.map((time) => (
														<SelectItem key={time} value={time}>
															{time.split(':')[0]}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</>
					)}
				{!showCampaignCapabilityLock &&
					!readOnly &&
					(rruleType === 'one_per_day' || rruleType === 'one_time_only') && (
						<FormField
							control={form.control}
							name="timeFrom"
							render={({ field }) => {
								const [hourPart, minutePart] = (field.value || '').split(':')

								const hourOptions = Array.from(
									{ length: 24 },
									(_, h) => `${String(h).padStart(2, '0')}`
								)
								const minuteOptions = Array.from(
									{ length: 60 },
									(_, m) => `${String(m).padStart(2, '0')}`
								)

								return (
									<FormItem>
										<FormLabel>{t('config.form.time')}</FormLabel>
										<div className="flex gap-2">
											<div className="flex flex-col w-1/2">
												<FormLabel className="text-xs text-muted-foreground">
													{t('config.form.hour')}
												</FormLabel>
												<Select
													onValueChange={(newHour) =>
														field.onChange(`${newHour}:${minutePart || '00'}`)
													}
													value={hourPart || undefined}
												>
													<SelectTrigger className="w-full md:min-w-[80px] bg-outline">
														<SelectValue placeholder="HH" />
													</SelectTrigger>
													<SelectContent>
														{hourOptions.map((h) => (
															<SelectItem key={h} value={h}>
																{h}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
											<div className="flex flex-col w-1/2">
												<FormLabel className="text-xs text-muted-foreground">
													{t('config.form.minute')}
												</FormLabel>
												<Select
													onValueChange={(newMinute) =>
														field.onChange(`${hourPart || '00'}:${newMinute}`)
													}
													value={minutePart || undefined}
												>
													<SelectTrigger className="w-full md:min-w-[80px] bg-outline">
														<SelectValue placeholder="MM" />
													</SelectTrigger>
													<SelectContent>
														{minuteOptions.map((m) => (
															<SelectItem key={m} value={m}>
																{m}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
										</div>
										<FormMessage />
									</FormItem>
								)
							}}
						/>
					)}
				{!showCampaignCapabilityLock && !readOnly && (
					<div className="flex gap-8">
						<FormField
							control={form.control}
							name="priority"
							render={({ field }) => (
								<FormItem className="flex items-center gap-2 space-y-0">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
									<FormLabel className="cursor-pointer">
										{t('config.form.prioritize')}
									</FormLabel>
									<Tooltip
										title={t('config.form.priorityTooltip')}
										className="w-60"
									>
										<Info className="w-2 h-4 text-muted-foreground cursor-help" />
									</Tooltip>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="interruptsMusic"
							render={({ field }) => (
								<FormItem className="flex items-center gap-2 space-y-0">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
									<FormLabel className="cursor-pointer">
										{t('config.form.interruptsMusic')}
									</FormLabel>
								</FormItem>
							)}
						/>
					</div>
				)}
				{!showCampaignCapabilityLock &&
					(rruleType === 'one_per_day' || rruleType === 'recurrent') && (
						<FormField
							control={form.control}
							name="selectedDays"
							render={() => (
								<FormItem className={readOnly ? 'hidden md:block' : ''}>
									<FormLabel>{t('config.form.playDays')}</FormLabel>
									<div className="flex mt-2 gap-4 flex-wrap">
										{DAYS_OF_WEEK.map((day) => (
											<FormField
												key={day.value}
												control={form.control}
												name="selectedDays"
												render={({ field }) => (
													<FormItem className="flex items-center gap-2 space-y-0">
														<FormControl>
															<Checkbox
																checked={
																	field.value?.includes(day.value) ?? false
																}
																onCheckedChange={(checked) => {
																	const currentValue = field.value || []
																	if (checked) {
																		field.onChange([...currentValue, day.value])
																	} else {
																		field.onChange(
																			currentValue.filter(
																				(v) => v !== day.value
																			)
																		)
																	}
																}}
															/>
														</FormControl>
														<FormLabel className="font-normal cursor-pointer">
															{t(`config.days.${day.value}`)}
														</FormLabel>
													</FormItem>
												)}
											/>
										))}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				{!showCampaignCapabilityLock &&
					!readOnly &&
					(rruleType === 'one_per_day' || rruleType === 'recurrent') &&
					!readOnly && (
						<div className="mt-12">
							<FormLabel className="mb-1 block">
								{t('config.form.exceptionDates')}
							</FormLabel>
							<Popover>
								<PopoverTrigger asChild>
									<FormControl>
										<Button
											variant="outline"
											className="w-full justify-start font-normal"
										>
											<CalendarIcon className="mr-2 h-4 w-4" />
											{t('config.form.addException')}
										</Button>
									</FormControl>
								</PopoverTrigger>
								<PopoverContent className="w-auto p-0">
									<Calendar
										mode="single"
										selected={undefined}
										onSelect={(date) => {
											if (date) {
												const selectedDate = DateTime.fromJSDate(date)
												const today = DateTime.now().startOf('day')
												// Do not allow past dates or today
												if (selectedDate.startOf('day') <= today) {
													return
												}

												// Respect configured campaign range when available
												if (startDate) {
													const start =
														DateTime.fromJSDate(startDate).startOf('day')
													if (selectedDate < start) {
														return
													}
												}
												if (endDate) {
													const end = DateTime.fromJSDate(endDate).endOf('day')
													if (selectedDate > end) {
														return
													}
												}

												const dateStr = selectedDate.toFormat('yyyy-MM-dd')
												const currentExdate = form.getValues('exdate')
												if (!currentExdate.includes(dateStr)) {
													form.setValue('exdate', [...currentExdate, dateStr])
												}
											}
										}}
										disabled={(day) => {
											const date = DateTime.fromJSDate(day).startOf('day')
											const today = DateTime.now().startOf('day')

											// Disable all past dates and today
											if (date <= today) {
												return true
											}

											// If campaign range is defined, disable outside of it
											if (startDate) {
												const start =
													DateTime.fromJSDate(startDate).startOf('day')
												if (date < start) {
													return true
												}
											}
											if (endDate) {
												const end = DateTime.fromJSDate(endDate).endOf('day')
												if (date > end) {
													return true
												}
											}

											return false
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>
							{exdate && exdate.length > 0 && !readOnly && (
								<div className="flex flex-wrap gap-2 mt-3">
									{exdate.map((date) => (
										<div
											key={date}
											className="flex items-center gap-2 px-3 py-1 bg-secondary rounded-full border text-sm"
										>
											{DateTime.fromISO(date ?? '').toFormat('MMM d, yyyy')}
											<Button
												type="button"
												variant="secondary"
												onClick={() => {
													const currentExdate = form.getValues('exdate')
													form.setValue(
														'exdate',
														currentExdate.filter((d) => d !== date)
													)
												}}
												className="ml-1 bg-surface-light"
											>
												<X className="w-3 h-3" />
											</Button>
										</div>
									))}
								</div>
							)}
						</div>
					)}
				{!readOnly && (
					<div className="hidden md:flex gap-2 pt-6">
						<Button
							type="button"
							variant="secondary"
							onClick={onCancel}
							className="rounded-full border px-8"
							disabled={isSubmitting}
						>
							{t('config.form.cancel')}
						</Button>
						<Button
							type="submit"
							className="rounded-full px-8"
							disabled={isSubmitting || showCampaignCapabilityLock}
						>
							{submitLabel}
						</Button>
					</div>
				)}
				{!readOnly && (
					<div className="h-24 flex items-center justify-center md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 bg-card border-t border-border">
						<Button
							type="submit"
							className="rounded-full w-full"
							disabled={isSubmitting || showCampaignCapabilityLock}
						>
							{submitLabel}
						</Button>
					</div>
				)}
			</form>
		</Form>
	)
}
